import React from "react";
import * as Sentry from "@sentry/react";
import { elementType, func, node, object, objectOf, string } from "prop-types";

import getDisplayName from "src/shared/utilities/getDisplayName";

import ErrorCaught from "../ErrorCaught";

const SentryErrorBoundary = ({
  children,
  onErrorRender,
  onReactError,
  Wrapper = React.Fragment,
  tags,
  componentProps
}) => {
  const handleErrorRender =
    onErrorRender ||
    (() => (
      <Wrapper>
        <ErrorCaught />
      </Wrapper>
    ));

  const handleReactError =
    onReactError ||
    ((error, componentStack, eventId) => {
      console.error({ componentStack, error, eventId });
    });

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        if (tags) {
          scope.setTags(tags);
        }
        scope.setExtras({ componentProps });
      }}
      fallback={handleErrorRender}
      onError={handleReactError}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
SentryErrorBoundary.propTypes = {
  children: node.isRequired,
  Wrapper: elementType,
  onReactError: func,
  onErrorRender: func,
  tags: objectOf(string),
  // eslint-disable-next-line react/forbid-prop-types
  componentProps: object
};

SentryErrorBoundary.defaultProps = {
  Wrapper: React.Fragment,
  onReactError: null,
  onErrorRender: null,
  tags: null,
  componentProps: null
};
export default SentryErrorBoundary;

export const withSentryErrorBoundary = (Component, options = {}) => {
  const WithSentryErrorBoundary = props => (
    // eslint-disable-next-line react/prop-types
    <SentryErrorBoundary {...options} componentProps={props}>
      <Component {...props} />
    </SentryErrorBoundary>
  );
  const componentName = getDisplayName(Component);

  WithSentryErrorBoundary.displayName = `${componentName}WithSentryErrorBoundary`;

  return WithSentryErrorBoundary;
};
